import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { combineLatest, Observable } from 'rxjs';
import { ListServerPaginationStateModel, LoadDataFacade, SetInitialStateFacade } from '../../../../../../common/models';
import {
  CompartmentUserListInitialState,
  CompartmentAssignedUserListLoad,
  CompartmentAvailableUserListLoad,
  CompartmentUserListAssignUsers,
  CompartmentUserListResetAvailableList,
  CompartmentUserListDeassignUsers,
  CompartmentUserListUpdateUserPermissions,
} from './compartment-user-list.actions';
import { CompartmentUserListState } from './compartment-user-list.state';
import { CompartmentAssignedUserListSearchCriteria, CompartmentAvailableUserListSearchCriteria } from '../../models';
import { ListSearchCriteriaFacade, ListSearchCriteriaType } from '../../../../list-search-criteria';
import { HubItemFacade } from '../../../hub/item';
import { CompartmentItemFacade } from '../../../compartment/item/compartment-item.facade';
import { take } from 'rxjs/operators';
import { MatDialogRef } from '@angular/material/dialog';
import { UsersSelectionDialogComponent } from '../../../../../../container-components/users-selection-dialog/users-selection-dialog.component';
import { UserSelectionItem } from '../../../../../../container-components';
import { Permission, PERMISSION_CONFIG } from '../../../../../../common';
import { AuthFacade } from '../../../../auth';

@Injectable({ providedIn: 'root' })
export class CompartmentUserListFacade implements LoadDataFacade, SetInitialStateFacade {
  assignedListDto$: Observable<ListServerPaginationStateModel<UserSelectionItem>> = this.store.select(
    CompartmentUserListState.getAssignedUsers,
  );
  availableListDto$: Observable<ListServerPaginationStateModel<UserSelectionItem>> = this.store.select(
    CompartmentUserListState.getAvailableUsers,
  );
  loaded$: Observable<boolean> = this.store.select(CompartmentUserListState.getLoaded);
  hasPermissionToUsers$ = this.authFacade.hasPermission$(
    PERMISSION_CONFIG[Permission.Administration][Permission.UserManagement][Permission.Self],
  );

  compartmentRelationIds$: Observable<[number, number]> = combineLatest([
    this.hubItemFacade.hubId$,
    this.compartmentItemFacade.compartmentId$,
  ]).pipe(take(1));

  constructor(
    private store: Store,
    private hubItemFacade: HubItemFacade,
    private compartmentItemFacade: CompartmentItemFacade,
    private listSearchCriteriaFacade: ListSearchCriteriaFacade,
    private authFacade: AuthFacade,
  ) {}

  loadAll(): void {
    combineLatest([
      this.hubItemFacade.hubId$,
      this.compartmentItemFacade.compartmentId$,
      this.listSearchCriteriaFacade.getSearchCriteriaByListType(ListSearchCriteriaType.CompartmentAssignedUsers),
    ])
      .pipe(take(1))
      .subscribe(
        ([hubId, compartmentId, searchCriteria]: [number, number, CompartmentAssignedUserListSearchCriteria]) => {
          this.store.dispatch(
            new CompartmentAssignedUserListLoad({
              hubId,
              compartmentId,
              searchCriteria,
            }),
          );
        },
      );
  }

  loadAvailableUserList(searchCriteria: CompartmentAvailableUserListSearchCriteria): void {
    this.compartmentRelationIds$.subscribe(([hubId, compartmentId]: [number, number]) => {
      this.store.dispatch(
        new CompartmentAvailableUserListLoad({
          hubId,
          compartmentId,
          searchCriteria,
        }),
      );
    });
  }

  assignUsersToCompartment(dialog: MatDialogRef<UsersSelectionDialogComponent>, users: UserSelectionItem[]): void {
    this.compartmentRelationIds$.subscribe(([hubId, compartmentId]: [number, number]) => {
      this.store.dispatch(
        new CompartmentUserListAssignUsers({
          dialog,
          hubId,
          compartmentId,
          users,
        }),
      );
    });
  }

  deassignUsersFromCompartment(users: UserSelectionItem[]): void {
    this.compartmentRelationIds$.subscribe(([hubId, compartmentId]: [number, number]) => {
      this.store.dispatch(
        new CompartmentUserListDeassignUsers({
          hubId,
          compartmentId,
          users,
        }),
      );
    });
  }

  updateUserPermissions(user: UserSelectionItem): void {
    this.compartmentRelationIds$.subscribe(([hubId, compartmentId]: [number, number]) => {
      this.store.dispatch(
        new CompartmentUserListUpdateUserPermissions({
          hubId,
          compartmentId,
          user,
        }),
      );
    });
  }

  resetAvailableList(): void {
    this.store.dispatch(new CompartmentUserListResetAvailableList());
  }

  setInitialState(): void {
    this.store.dispatch(new CompartmentUserListInitialState());
  }
}
